import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Crown, Gem, Building2, Check, Loader2, Clock, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_URL, withCredentials: true });

const TIER_UI = {
  starter: { Icon: Sparkles, accent: '#3B82F6', name: 'Starter', tagline: 'Best for launching your brand' },
  pro: { Icon: Crown, accent: '#7C3AED', name: 'Pro', tagline: 'Best for active business owners', highlight: true },
  premium: { Icon: Gem, accent: '#0EA5E9', name: 'Premium', tagline: 'Best for power users and growing teams' },
};
const RANK = { starter: 1, pro: 2, premium: 3, agency: 4 };

const baseFeatures = (p) => [
  `${p.posts_per_day} posts / day`,
  `${p.videos_per_day} video${p.videos_per_day === 1 ? '' : 's'} / day`,
  `${p.max_brand_profiles == null ? 'Unlimited' : p.max_brand_profiles} brand profile${p.max_brand_profiles === 1 ? '' : 's'}`,
  `${p.max_library_uploads == null ? 'Unlimited' : p.max_library_uploads} library uploads`,
  `Content plans up to ${p.max_content_plan_days} days`,
  'Trends, Creator Analysis & Performance included',
];
const PAID_EXTRA = ['Full access to advanced tools', 'Scheduler & auto-publishing coming soon'];

export default function StudioPricingSection({ compact = false }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myTier, setMyTier] = useState(null);
  const [busyTier, setBusyTier] = useState(null);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const resumeHandled = React.useRef(false);

  const loadMyTier = useCallback(async () => {
    if (!user || isAdmin) { setMyTier(isAdmin ? 'admin' : null); return; }
    try {
      const r = await api.get('/api/admin/social-studio/usage');
      setMyTier(r.data?.tier || null);
    } catch {
      setMyTier(null); // logged-in but not a subscriber yet
    }
  }, [user, isAdmin]);

  useEffect(() => {
    axios.get(`${API_URL}/api/social-studio/public-plans`)
      .then((r) => setPlans(r.data?.plans || []))
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
    loadMyTier();
  }, [loadMyTier]);

  const handlePlan = async (tier) => {
    if (isAdmin) { navigate('/admin/social-studio'); return; }
    if (!user) {
      try { localStorage.setItem('studio_subscribe_tier', tier); } catch (_e) { /* noop */ }
      navigate('/register');
      return;
    }
    if (myTier === tier) return; // already current
    if (myTier && RANK[tier] < RANK[myTier]) {
      const ok = window.confirm(`Switch down to the ${TIER_UI[tier]?.name} plan? Your billing will be prorated and your limits will change to the ${TIER_UI[tier]?.name} tier.`);
      if (!ok) return;
    }
    setBusyTier(tier);
    try {
      const r = await api.post('/api/social-studio/subscribe/checkout', { tier, origin_url: window.location.origin });
      if (r.data?.url) { window.location.href = r.data.url; return; }
      if (r.data?.changed) {
        toast.success(`You're now on the ${r.data.plan_name || TIER_UI[tier]?.name} plan.`);
        await loadMyTier();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not change plan. Please try again.');
    } finally {
      setBusyTier(null);
    }
  };

  // Resume from an abandoned-checkout recovery email link (/studio?resume=<tier>).
  // We never auto-trigger checkout; we only bring the member to the pricing cards so
  // they can choose to subscribe. (Logged-out users keep their pick for after sign-up.)
  useEffect(() => {
    if (resumeHandled.current || loading) return;
    let resume = null;
    try { resume = new URLSearchParams(window.location.search).get('resume'); } catch (_e) { /* noop */ }
    if (!resume || !['starter', 'pro', 'premium'].includes(resume)) return;
    resumeHandled.current = true;
    if (!user) {
      try { localStorage.setItem('studio_subscribe_tier', resume); } catch (_e) { /* noop */ }
    }
    document.getElementById('studio-pricing-section')?.scrollIntoView({ behavior: 'smooth' });
  }, [loading, user]);

  const ctaFor = (tier) => {
    if (isAdmin) return { label: 'Open Studio', disabled: false };
    if (myTier === tier) return { label: 'Current plan', disabled: true, current: true };
    if (myTier && RANK[tier] < RANK[myTier]) return { label: `Downgrade to ${TIER_UI[tier]?.name}`, disabled: false };
    if (myTier && RANK[tier] > RANK[myTier]) return { label: `Upgrade to ${TIER_UI[tier]?.name}`, disabled: false };
    return { label: tier === 'starter' ? 'Get Started' : `Get ${TIER_UI[tier]?.name}`, disabled: false };
  };

  return (
    <section id="studio-pricing-section" className="section-padding bg-[#0A0F1C] relative overflow-hidden" data-testid="studio-pricing-section">
      <div className="container-custom relative">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.14em] uppercase text-[#A78BFA] mb-3">
            <Sparkles size={14} /> Social Media Viral Studio
          </span>
          <h2 className="heading-2 text-3xl lg:text-4xl text-white mb-3">Create Social Media Content Faster With AI</h2>
          <p className="text-[#CBD5E1] text-base lg:text-lg">
            Generate posts, video scripts, hooks, content plans, and brand-specific ideas for your business, without staring at a blank screen.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#A78BFA]" size={28} /></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto items-stretch">
            {['starter', 'pro', 'premium'].map((tier) => {
              const p = plans.find((x) => x.tier === tier);
              if (!p) return null;
              const ui = TIER_UI[tier];
              const features = tier === 'starter' ? baseFeatures(p) : [...baseFeatures(p), ...PAID_EXTRA];
              const cta = ctaFor(tier);
              return (
                <PriceCard
                  key={tier} Icon={ui.Icon} accent={ui.accent} name={ui.name} price={p.price}
                  tagline={ui.tagline} highlight={ui.highlight} isCurrent={myTier === tier} features={features}
                  cta={cta.label} ctaDisabled={cta.disabled} busy={busyTier === tier}
                  onClick={() => handlePlan(tier)} dataTestid={`studio-price-${tier}`}
                />
              );
            })}
            <PriceCard
              Icon={Building2} accent="#2DD4BF" name="Agency / Multi-Brand" price={null}
              tagline="For coaches, consultants, agencies, churches and multi-brand entrepreneurs"
              features={['Higher daily limits than Premium', 'Unlimited brands and uploads', '90-day multi-brand workflow', 'Future team and client workspace support']}
              cta="Join the waitlist" comingSoon onClick={() => setWaitlistOpen(true)} dataTestid="studio-price-agency"
            />
          </div>
        )}

        {!compact && (
          <p className="text-center text-[#94A3B8] text-sm mt-8">Cancel anytime. Daily limits reset every morning.</p>
        )}
      </div>

      {waitlistOpen && <WaitlistModal onClose={() => setWaitlistOpen(false)} defaultEmail={user?.email || ''} />}
    </section>
  );
}

function PriceCard({ Icon, accent, name, price, tagline, features, cta, onClick, highlight, comingSoon, isCurrent, ctaDisabled, busy, dataTestid }) {
  return (
    <div
      className={`rounded-2xl p-6 flex flex-col ${highlight || isCurrent ? 'ring-2' : 'border border-white/10'}`}
      style={{ background: highlight ? 'linear-gradient(165deg, rgba(124,58,237,0.18), rgba(10,15,28,0.6))' : 'rgba(255,255,255,0.03)', boxShadow: isCurrent ? `0 0 0 2px ${accent}` : highlight ? '0 0 0 2px rgba(124,58,237,0.5)' : 'none' }}
      data-testid={dataTestid}
    >
      <div className="flex items-center justify-between mb-3 gap-2">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-white text-xs font-black" style={{ background: accent }}>
          <Icon size={13} /> {name}
        </span>
        {isCurrent && <span className="text-[10px] font-bold uppercase whitespace-nowrap" style={{ color: accent }} data-testid={`${dataTestid}-current`}>Current plan</span>}
        {!isCurrent && highlight && <span className="text-[10px] font-bold uppercase text-[#A78BFA] whitespace-nowrap">Most popular</span>}
        {comingSoon && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-[#2DD4BF] whitespace-nowrap"><Clock size={11} /> Coming soon</span>}
      </div>
      <p className="text-white text-3xl font-light mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {price == null ? <span className="text-xl text-[#94A3B8]">Coming soon</span> : <>${Number(price).toFixed(0)}<span className="text-base text-[#94A3B8]">/mo</span></>}
      </p>
      <p className="text-[#94A3B8] text-sm mb-4">{tagline}</p>
      <ul className="space-y-2 text-sm text-[#CBD5E1] flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2"><Check size={15} className="mt-0.5 flex-shrink-0" style={{ color: accent }} /> {f}</li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onClick}
        disabled={ctaDisabled || busy}
        data-testid={`${dataTestid}-cta`}
        className={`mt-6 w-full font-bold py-3 rounded-lg transition-colors inline-flex items-center justify-center gap-2 ${ctaDisabled ? 'bg-white/10 text-white/70 cursor-default' : comingSoon ? 'bg-white/10 text-white hover:bg-white/20' : highlight ? 'bg-[#7C3AED] hover:bg-[#6D28D9] text-white' : 'bg-white text-[#0A0F1C] hover:bg-[#E2E8F0]'} disabled:opacity-80`}
      >
        {busy && <Loader2 size={16} className="animate-spin" />} {cta}
      </button>
    </div>
  );
}

function WaitlistModal({ onClose, defaultEmail }) {
  const [email, setEmail] = useState(defaultEmail || '');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/social-studio/waitlist`, { email: email.trim(), name: name.trim(), plan: 'agency' });
      setDone(true);
      toast.success(r.data?.message || "You're on the waitlist!");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not join the waitlist. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="waitlist-modal">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md relative" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#94A3B8] hover:text-[#0A0F1C]" data-testid="waitlist-close" aria-label="Close">
          <X size={20} />
        </button>
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-white text-xs font-black mb-3" style={{ background: '#2DD4BF' }}>
          <Building2 size={13} /> Agency / Multi-Brand
        </span>
        {done ? (
          <div className="text-center py-4" data-testid="waitlist-success">
            <Check size={40} className="text-emerald-500 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-[#0A0F1C] mb-1">You are on the list</h3>
            <p className="text-sm text-[#475569]">We will email you the moment the Agency plan goes live.</p>
            <button type="button" onClick={onClose} className="mt-5 bg-[#0A0F1C] text-white font-bold px-6 py-2.5 rounded-lg hover:bg-black transition-colors">Done</button>
          </div>
        ) : (
          <>
            <h3 className="text-xl font-bold text-[#0A0F1C] mb-1">Join the Agency waitlist</h3>
            <p className="text-sm text-[#475569] mb-4">The Agency / Multi-Brand plan is coming soon. Drop your email and you will be first to know.</p>
            <form onSubmit={submit} className="space-y-3">
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)"
                data-testid="waitlist-name" className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:border-[#2DD4BF] outline-none" />
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@business.com"
                data-testid="waitlist-email" className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:border-[#2DD4BF] outline-none" />
              <button type="submit" disabled={busy} data-testid="waitlist-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#0F766E] hover:bg-[#0e6b63] text-white font-bold py-3 rounded-lg transition-colors disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Building2 size={16} />} Join the waitlist
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
